import axios from "axios";
import { API_BASE_URL, API_BASE_URL1, FILE_BASE_URL } from "../Services/baseApiUrl";
import axiosInstance from "../../Hooks/axoisInstance";
import { getAllComplaintZones, getComplaintCirclesByZoneId } from "./complaintApi";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const postFormData = async (url, formData) => {
  const response = await axios.post(url, formData, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

const putFormData = async (url, formData) => {
  const response = await axios.put(url, formData, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

const toList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const IE_COMPLAINT_ENDPOINTS = {
  GET_ALL: `${API_BASE_URL}IEComplaint/GetAll`,
  GET_BY_ID: `${API_BASE_URL}IEComplaint/GetById`,
  ADD: `${API_BASE_URL}IEComplaint/Add`,
  UPDATE: `${API_BASE_URL}IEComplaint/Update`,
  CLOSE: `${API_BASE_URL}IEComplaint/CloseComplaint`,
  GET_CLOSED: `${API_BASE_URL}IEComplaint/GetClosedComplaints`,
  GET_COLONIES: `${API_BASE_URL}IEComplaint/GetColoniesByCircle`,
  GET_STATUS: `${API_BASE_URL}IEComplaint/GetStatusDropdowns`,
  GET_STATUS_FILTER: `${API_BASE_URL}IEComplaint/GetStatusDropdown`,
  GET_ISSUE_TYPES: `${API_BASE_URL}IEComplaint/GetActiveIssueTypes`,
  GET_KPI_DETAILS: `${API_BASE_URL1}Attendance/GetKPIDetails`,
};

export const resolveIeComplaintImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  if (imagePath.startsWith("/")) {
    return `${API_BASE_URL.replace(/\/$/, "")}${imagePath}`;
  }
  return `${FILE_BASE_URL}${imagePath}`;
};

const buildIeComplaintFormData = (values, { isEdit = false } = {}) => {
  const formData = new FormData();

  formData.append("KPITypeId", String(values.kpiTypeId ?? ""));
  formData.append("ZoneId", String(values.zoneId ?? ""));
  formData.append("WardId", String(values.wardId ?? ""));
  formData.append("IssueTypeId", String(values.issueTypeId ?? ""));
  formData.append("ColonyId", String(values.colonyId ?? ""));
  formData.append("ContactNo", values.contactNo ?? "");
  formData.append(
    "ComplaintDate",
    values.complaintCreatedOn?.includes("T")
      ? values.complaintCreatedOn.split("T")[0]
      : values.complaintCreatedOn ?? ""
  );
  formData.append("FLat", String(values.latitude ?? ""));
  formData.append("FLng", String(values.longitude ?? ""));
  formData.append("FAddress", values.location ?? "");
  formData.append("Remarks", values.actionRemarks ?? "");
  formData.append("DescriptionBefore", values.description ?? "");

  if (!isEdit) {
    formData.append("ModeOfReporting", values.modeOfReporting || "WEB");
  }

  if (values.imageFile instanceof File) {
    const isCloseImageUpload =
      isEdit && (values.status === "Closed" || values.status === "Invalid");
    if (!isCloseImageUpload) {
      formData.append("File", values.imageFile);
    }
  }

  if (isEdit) {
    formData.append("IeComplaintId", String(values.complaintId ?? ""));
  }

  return formData;
};

const buildCloseComplaintFormData = (values) => {
  const formData = new FormData();
  const isClosed = values.status === "Closed";
  const isInvalid = values.status === "Invalid";

  formData.append(
    "ComplaintCode",
    values.complainantNo ?? values.complaintcode ?? values.complaintCode ?? ""
  );
  formData.append("Remarks", values.actionRemarks ?? values.description ?? "");
  const completionLat =
    values.closeTimeLatitude || values.cLat || values.latitude || "";
  const completionLng =
    values.closeTimeLongitude || values.cLng || values.longitude || "";

  formData.append("CompletionLat", String(completionLat));
  formData.append("CompletionLng", String(completionLng));
  formData.append(
    "CompletionAddress",
    values.closeTimeLocation ?? values.cAddress ?? values.location ?? ""
  );
  formData.append("IsClosed", isClosed ? "1" : isInvalid ? "2" : "0");

  if (values.imageFile instanceof File && (isClosed || isInvalid)) {
    formData.append("CompletionImage", values.imageFile);
  }

  return formData;
};

export const getIeComplaintKpiDetails = async () => {
  const response = await axiosInstance.get(IE_COMPLAINT_ENDPOINTS.GET_KPI_DETAILS);
  return toList(response.data);
};

export const getIeComplaintZones = getAllComplaintZones;

export const getIeComplaintWardsByZoneId = getComplaintCirclesByZoneId;

export const getIeComplaintColoniesByCircle = async (circleId) => {
  if (!circleId) return [];
  const response = await axiosInstance.get(
    `${IE_COMPLAINT_ENDPOINTS.GET_COLONIES}/${circleId}`
  );
  return toList(response.data);
};

export const getIeComplaintStatusOptions = async () => {
  const response = await axiosInstance.get(IE_COMPLAINT_ENDPOINTS.GET_STATUS);
  return toList(response.data).map((item) => ({
    id: item.id ?? item.statusId ?? "",
    name: item.name ?? item.status ?? "",
  }));
};

export const getIeComplaintStatusFilterOptions = async () => {
  const response = await axiosInstance.get(IE_COMPLAINT_ENDPOINTS.GET_STATUS_FILTER);
  return toList(response.data).map((item) => ({
    id: item.id ?? item.statusId ?? "",
    name: item.name ?? item.status ?? "",
  }));
};

export const getIeComplaintIssueTypes = async (kpiTypeId) => {
  if (!kpiTypeId) return [];
  const response = await axiosInstance.get(IE_COMPLAINT_ENDPOINTS.GET_ISSUE_TYPES, {
    params: { KPITypeId: kpiTypeId },
  });
  return toList(response.data);
};

export const getIeComplaintList = async ({
  zoneId = "",
  wardId = "",
  kpiTypeId = "",
  status = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 100,
} = {}) => {
  const params = {
    pageNumber,
    pageSize,
  };

  if (zoneId) params.ZoneId = zoneId;
  if (wardId) params.WardId = wardId;
  if (kpiTypeId) params.KpiTypeId = kpiTypeId;
  if (status) params.Status = status;
  if (fromDate) params.FromDate = fromDate;
  if (toDate) params.ToDate = toDate;

  const response = await axiosInstance.get(IE_COMPLAINT_ENDPOINTS.GET_ALL, {
    params,
  });
  return response.data;
};

export const getIeComplaintById = async (complaintId) => {
  const response = await axiosInstance.get(
    `${IE_COMPLAINT_ENDPOINTS.GET_BY_ID}/${complaintId}`
  );
  return response.data;
};

export const getIeComplaintClosedHistory = async (complaintCode) => {
  if (!complaintCode) {
    return { success: true, count: 0, data: [] };
  }

  const response = await axiosInstance.get(IE_COMPLAINT_ENDPOINTS.GET_CLOSED, {
    params: { complaintcode: complaintCode },
  });
  return response.data;
};

export const addIeComplaint = async (values) =>
  postFormData(IE_COMPLAINT_ENDPOINTS.ADD, buildIeComplaintFormData(values));

export const updateIeComplaint = async (values) =>
  putFormData(
    IE_COMPLAINT_ENDPOINTS.UPDATE,
    buildIeComplaintFormData(values, { isEdit: true })
  );

export const closeIeComplaint = async (values) =>
  putFormData(IE_COMPLAINT_ENDPOINTS.CLOSE, buildCloseComplaintFormData(values));
